<template>
  <div class="wf-sidebar-config" v-if="selectedCanvasNode !== null">
    <div class="wf-sidebar-config-title">Configuration</div>
    <div class="wf-config-row">
      <label>Label</label>
      <input class="wf-input" :value="selectedCanvasNode.data.label" @input="updateSelectedNodeData({ label: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="(selectedCanvasNode.data.nodeType === 'Trigger' && ['onCallEnd', 'onCallAnalysis'].includes(selectedCanvasNode.data.triggerType) === true) || (selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType !== 'sendConversation')">
      <MultiSelect
        :model-value="getNodeSelectedAgentIds(selectedCanvasNode.data)"
        @update:modelValue="updateSelectedNodeAgentIdsByModel"
        :options="availableAgents"
        label="Run on Agents"
        placeholder="Select agents"
        id-field="agentid"
        label-field="agentname"
        :is-loading="availableAgents.length === 0"
        button-class="wf-agent-multiselect-btn"
      />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger'">
      <label>Trigger</label>
      <select class="wf-select" :value="selectedCanvasNode.data.triggerType" @change="updateNodeTriggerType(selectedCanvasNode.id, $event.target.value)">
        <option value="onCallEnd">onCallEnd</option>
        <option value="onCallAnalysis">onCallAnalysis</option>
        <option value="onWebhook">onWebhook</option>
        <option value="onTimer">onTimer</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger' && selectedCanvasNode.data.triggerType === 'onTimer'">
      <label>Mode</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.mode" @change="updateSelectedNodeConfig({ mode: $event.target.value })">
        <option value="delay">delay</option>
        <option value="interval">interval</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger' && selectedCanvasNode.data.triggerType === 'onTimer' && selectedCanvasNode.data.config?.mode === 'delay'">
      <label>Delay Value</label>
      <input class="wf-input" type="number" min="1" :value="selectedCanvasNode.data.config?.delayValue" @input="updateSelectedNodeConfig({ delayValue: Number.parseInt($event.target.value, 10) })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger' && selectedCanvasNode.data.triggerType === 'onTimer' && selectedCanvasNode.data.config?.mode === 'delay'">
      <label>Delay Unit</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.delayUnit" @change="updateSelectedNodeConfig({ delayUnit: $event.target.value })">
        <option value="seconds">seconds</option>
        <option value="minutes">minutes</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger' && selectedCanvasNode.data.triggerType === 'onTimer' && selectedCanvasNode.data.config?.mode === 'interval'">
      <label>Each Minutes</label>
      <input class="wf-input" type="number" min="1" :value="selectedCanvasNode.data.config?.intervalMinutes" @input="updateSelectedNodeConfig({ intervalMinutes: Number.parseInt($event.target.value, 10) })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Trigger' && selectedCanvasNode.data.triggerType === 'onWebhook'">
      <div class="flex items-center justify-between mb-1">
        <label>Webhook URL</label>
        <button class="btn btn-ghost btn-xs" @click="showWebhookHelp = true" title="Show payload example">
          <Icon icon="tabler:question-circle" width="16" />
        </button>
      </div>
      <div class="flex space-x-2" v-if="workflowGuid">
        <input class="wf-input flex-grow font-mono text-xs" readonly :value="`${apiEndpoint}/api/workflows/webhook/${workflowGuid}`" />
        <button class="btn btn-ghost btn-xs p-1" @click="copyWebhookUrl(`${apiEndpoint}/api/workflows/webhook/${workflowGuid}`)" title="Copy URL">
          <Icon icon="tabler:copy" width="16" />
        </button>
      </div>
      <div v-else class="text-xs text-orange-500 italic">
        Save workflow first to generate webhook URL
      </div>
    </div>

    <AppModal :visible="showWebhookHelp === true" title="Webhook Payload Example" @close="showWebhookHelp = false" box-class="max-w-md">
      <div class="p-4 overflow-y-auto max-h-[70vh]">
        <p class="text-sm mb-4">
          Send a <strong>POST</strong> request to the generated URL with a JSON body. 
          The workflow will receive all fields as variables that you can use in subsequent steps.
        </p>
        <p class="text-sm font-semibold mb-2">Example Payload:</p>
        <pre class="bg-base-200 p-3 rounded-md text-xs font-mono mb-4 whitespace-pre-wrap">
{
  "customerName": "John Doe",
  "orderId": "12345",
  "status": "shipped",
  "items": [
    {"id": "p1", "price": 29.99},
    {"id": "p2", "price": 15.50}
  ]
}</pre>
        <p class="text-sm">
          All top-level keys (like <code>customerName</code>, <code>orderId</code>) will be available directly as workflow variables. 
          Nested objects and arrays are also supported.
        </p>
      </div>
      <template #actions>
        <button class="btn btn-sm" @click="showWebhookHelp = false">Close</button>
      </template>
    </AppModal>

    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If'">
      <label>Source</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.sourceType || 'variable'" @change="updateSelectedNodeConfig({ sourceType: $event.target.value })">
        <option value="variable">variable</option>
        <option value="conversation">conversation</option>
        <option value="tag">tag</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If' && (selectedCanvasNode.data.config?.sourceType || 'variable') === 'variable'">
      <label>Variable</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.sourceKey || ''" @change="updateSelectedNodeConfig({ sourceKey: $event.target.value })">
        <option value="">Select variable</option>
        <option v-for="variableOption in workflowVariableOptions" :key="`if-variable-option-${variableOption.id}`" :value="variableOption.value">{{ variableOption.label }}</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If' && (selectedCanvasNode.data.config?.sourceType || 'variable') === 'conversation'">
      <label>Conversation</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.sourceKey || ''" @change="updateSelectedNodeConfig({ sourceKey: $event.target.value })">
        <option value="">Select conversation source</option>
        <option v-for="conversationOption in workflowConversationOptions" :key="`if-conversation-option-${conversationOption.id}`" :value="conversationOption.value">{{ conversationOption.label }}</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If' && (selectedCanvasNode.data.config?.sourceType || 'variable') === 'tag'">
      <label>Tag</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.tagValue || ''" @change="updateSelectedNodeConfig({ tagValue: $event.target.value })">
        <option value="">Select tag</option>
        <option v-for="tagOption in workflowTagOptions" :key="`if-tag-option-${tagOption.id}`" :value="tagOption.value">{{ tagOption.label }}</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If'">
      <label>Operator</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.operator" @change="updateSelectedNodeConfig({ operator: $event.target.value })">
        <option value="equals">equals</option>
        <option value="notEquals">notEquals</option>
        <option value="contains">contains</option>
        <option value="greaterThan">greaterThan</option>
        <option value="lessThan">lessThan</option>
        <option value="exists">exists</option>
        <option value="isEmpty">isEmpty</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'If'">
      <label>Right</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.right" @input="updateSelectedNodeConfig({ right: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action'">
      <label>Action Type</label>
      <select class="wf-select" :value="selectedCanvasNode.data.actionType" @change="updateNodeActionType(selectedCanvasNode.id, $event.target.value)">
        <option value="activateTool">activateTool</option>
        <option value="httpRequest">httpRequest</option>
        <option value="sendEmail">sendEmail</option>
        <option value="sendSms">sendSms</option>
        <option value="queryLlm">queryLlm</option>
        <option value="executeJs">executeJs</option>
        <option value="sendConversation">sendConversation</option>
        <option value="manageVariable">manageVariable</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'activateTool'">
      <label>Tool</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.toolId" @change="handleSelectActivateTool($event.target.value)">
        <option value="">Select tool</option>
        <option v-for="tool in availableToolsSelectOptions" :key="`tool-option-${tool.id}`" :value="tool.id">{{ tool.label }}</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'httpRequest'">
      <label>Method</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.method" @change="updateSelectedNodeConfig({ method: $event.target.value })">
        <option value="GET">GET</option>
        <option value="POST">POST</option>
        <option value="PUT">UPDATE</option>
        <option value="PATCH">PATCH</option>
        <option value="DELETE">DELETE</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'httpRequest'">
      <label>URL</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.url" @input="updateSelectedNodeConfig({ url: $event.target.value })" placeholder="https://..." />
      <div class="node-inline-hint">Variables supported: &#123;&#123;variableName&#125;&#125;</div>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'httpRequest'">
      <label>Headers</label>
      <div v-for="(header, index) in (selectedCanvasNode.data.config?.headersList || [])" :key="'header-'+index" class="flex space-x-2 mb-2">
        <input class="wf-input flex-1" :value="header.key" @input="updateHeaderKey(selectedCanvasNode.id, index, $event.target.value)" placeholder="Header-Name" />
        <input class="wf-input flex-1" :value="header.value" @input="updateHeaderValue(selectedCanvasNode.id, index, $event.target.value)" placeholder="&#123;&#123;variableValue&#125;&#125;" />
        <button class="btn btn-sm btn-ghost text-red-500" @click="removeHeader(selectedCanvasNode.id, index)">
          <Icon icon="tabler:trash" width="16" />
        </button>
      </div>
      <button class="btn btn-sm btn-outline mt-1" @click="addHeader(selectedCanvasNode.id)">+ Add Header</button>
      <div class="node-inline-hint">Header values support variables: &#123;&#123;variableName&#125;&#125;</div>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'httpRequest'">
      <label>Body (JSON)</label>
      <MonacoEditor 
        :modelValue="selectedCanvasNode.data.config?.body || '{}'" 
        @update:modelValue="val => updateSelectedNodeConfig({ body: val })" 
        height="150px" 
      />
      <div class="node-inline-hint">Use JSON with variables like &#123;&#123;customerId&#125;&#125;</div>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendEmail'">
      <label>To</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.to" @input="updateSelectedNodeConfig({ to: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendEmail'">
      <label>Provider</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.provider" @input="updateSelectedNodeConfig({ provider: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendSms'">
      <label>To</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.to" @input="updateSelectedNodeConfig({ to: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendSms'">
      <label>Message</label>
      <textarea class="wf-textarea" :value="selectedCanvasNode.data.config?.message" @input="updateSelectedNodeConfig({ message: $event.target.value })"></textarea>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendSms'">
      <label>Provider</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.provider || 'webhook'" @input="updateSelectedNodeConfig({ provider: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendSms'">
      <label>Webhook URL</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.webhookUrl" @input="updateSelectedNodeConfig({ webhookUrl: $event.target.value })" placeholder="https://..." />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'queryLlm'">
      <label>Prompt</label>
      <textarea class="wf-textarea" :value="selectedCanvasNode.data.config?.prompt" @input="updateSelectedNodeConfig({ prompt: $event.target.value })"></textarea>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'queryLlm'">
      <label>Provider</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.provider || 'webhook'" @input="updateSelectedNodeConfig({ provider: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'queryLlm'">
      <label>Webhook URL</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.webhookUrl" @input="updateSelectedNodeConfig({ webhookUrl: $event.target.value })" placeholder="https://..." />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'queryLlm'">
      <label>Response Path</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.responsePath" @input="updateSelectedNodeConfig({ responsePath: $event.target.value })" placeholder="answer" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'queryLlm'">
      <label>Output Variable</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.outputVariable" @input="updateSelectedNodeConfig({ outputVariable: $event.target.value })" placeholder="llmResult" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'executeJs'">
      <label>Script</label>
      <textarea class="wf-textarea" :value="selectedCanvasNode.data.config?.script" @input="updateSelectedNodeConfig({ script: $event.target.value })"></textarea>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'executeJs'">
      <label>Use Variables in JS</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.useVariables === true ? 'true' : 'false'" @change="updateSelectedNodeConfig({ useVariables: $event.target.value === 'true' })">
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendConversation'">
      <label>Webhook URL</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.address" @input="updateSelectedNodeConfig({ address: $event.target.value })" placeholder="https://..." />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'sendConversation'">
      <MultiSelect
        :model-value="getNodeSelectedConversationVariableIds(selectedCanvasNode.data)"
        @update:modelValue="updateSelectedNodeConversationVariableByModel"
        :options="workflowVariableOptions"
        label="Conversation Variable"
        placeholder="Select variable"
        id-field="value"
        label-field="label"
        :is-loading="workflowVariableOptions.length === 0"
        button-class="wf-agent-multiselect-btn"
        :max-selections="1"
        force-direction="ltr"
      />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable'">
      <label>Operation</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.operation || 'new'" @change="updateSelectedNodeConfig({ operation: $event.target.value })">
        <option value="new">new</option>
        <option value="update">update</option>
        <option value="increase">increase</option>
        <option value="remove">remove</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable'">
      <label>Variable Name</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.variableName" @input="updateSelectedNodeConfig({ variableName: $event.target.value })" placeholder="myVar" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable' && selectedCanvasNode.data.config?.operation !== 'remove' && selectedCanvasNode.data.config?.operation !== 'increase'">
      <label>Type</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.valueType || 'string'" @change="updateSelectedNodeConfig({ valueType: $event.target.value })">
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="conversation">conversation</option>
      </select>
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable' && selectedCanvasNode.data.config?.operation === 'increase'">
      <label>Increase By</label>
      <input class="wf-input" type="number" :value="selectedCanvasNode.data.config?.amount ?? 1" @input="updateSelectedNodeConfig({ amount: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable' && (selectedCanvasNode.data.config?.operation === 'new' || selectedCanvasNode.data.config?.operation === 'update') && selectedCanvasNode.data.config?.valueType !== 'conversation'">
      <label>Value</label>
      <input class="wf-input" :value="selectedCanvasNode.data.config?.value" @input="updateSelectedNodeConfig({ value: $event.target.value })" />
    </div>
    <div class="wf-config-row" v-if="selectedCanvasNode.data.nodeType === 'Action' && selectedCanvasNode.data.actionType === 'manageVariable' && (selectedCanvasNode.data.config?.operation === 'new' || selectedCanvasNode.data.config?.operation === 'update') && selectedCanvasNode.data.config?.valueType === 'conversation'">
      <label>From Variable</label>
      <select class="wf-select" :value="selectedCanvasNode.data.config?.fromVariable || ''" @change="updateSelectedNodeConfig({ fromVariable: $event.target.value })">
        <option value="">Select conversation variable</option>
        <option v-for="variableOption in workflowVariableOptions" :key="`sidebar-manage-variable-source-${variableOption.id}`" :value="variableOption.value">{{ variableOption.label }}</option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import MonacoEditor from '../MonacoEditor.vue';
import MultiSelect from '../MultiSelect.vue';
import AppModal from '../AppModal.vue';
import { useToast } from 'vue-toastification';

const props = defineProps({
  selectedCanvasNode: { type: Object, default: null },
  availableAgents: { type: Array, default: () => [] },
  availableToolsSelectOptions: { type: Array, default: () => [] },
  workflowVariableOptions: { type: Array, default: () => [] },
  workflowConversationOptions: { type: Array, default: () => [] },
  workflowTagOptions: { type: Array, default: () => [] },
  workflowGuid: { type: String, default: null },
  getNodeSelectedAgentIds: { type: Function, required: true },
  getNodeSelectedConversationVariableIds: { type: Function, required: true },
  updateSelectedNodeData: { type: Function, required: true },
  updateSelectedNodeConfig: { type: Function, required: true },
  updateSelectedNodeAgentIdsByModel: { type: Function, required: true },
  updateSelectedNodeConversationVariableByModel: { type: Function, required: true },
  updateNodeTriggerType: { type: Function, required: true },
  updateNodeActionType: { type: Function, required: true },
  handleSelectActivateTool: { type: Function, required: true },
  updateHeaderKey: { type: Function, required: true },
  updateHeaderValue: { type: Function, required: true },
  removeHeader: { type: Function, required: true },
  addHeader: { type: Function, required: true }
});

const toast = useToast();
const showWebhookHelp = ref(false);
const apiEndpoint = computed(() => {
  return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3000' : window.location.origin;
});

async function copyWebhookUrl(url) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied to clipboard');
  } catch (err) {
    toast.error('Failed to copy URL');
  }
}
</script>
